import { ApprovalRequest, ApprovalState } from '@/models/approval.model';
import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BinderService } from './binder.service';
import { AppAuthService } from '../app/auth.service';

export interface response {
  approvalId: number,
  approved:boolean,
  message:string
}

@Injectable({
  providedIn: 'root'
})
export class ApprovalsService {
  private baseUrl = environment.landmark_API_URL + '/approvals';
  currentRequest = signal<ApprovalRequest | null>(null);
  approvalState = signal<ApprovalState>('pending');
  currentStatus = signal<number>(0);  
  
  currentBinderService = inject(BinderService);
  private http: HttpClient = inject(HttpClient);
  private authService = inject(AppAuthService);

  constructor() {
    effect(() => {
      const binder = this.currentBinderService.currentBinder();

      if(binder && binder.approvalRequests && binder.approvalRequests.length > 0) {
        const pendingRequests = binder.approvalRequests
        const latestRequest = pendingRequests.reduce((latest, current) => 
          new Date(latest.createdOn) > new Date(current.createdOn) ? latest : current, pendingRequests[0]
        );
        this.setRequest(latestRequest, binder.ProjectStatus);
        this.currentStatus.set(binder.ProjectStatus);
      } else {
        // console.log('no approval requests found');
        this.currentRequest.set(null);
        this.approvalState.set('pending');
        
        this.currentStatus.set(0);
      }
      // console.log('approval state set to:', this.approvalState());
    });
  }

  getComputedApprovalState(): ApprovalState {
    const binder = this.currentBinderService.currentBinder();
    const request = this.currentRequest();
  
    if (!binder) return 'editing';
    const status = binder.ProjectStatus;
  
    if (status < 5) return 'editing';
    if (status === 5) {
      if (!request || request.approvalResponses.length === 0 || !request.approvalResponses) {
        return 'pending';
      }
      const latest = request.approvalResponses.reduce((a, b) =>
        new Date(a.responseDate) > new Date(b.responseDate) ? a : b
      );
      return latest.approved ? 'error' : 'rejected'; // Error: should never be approved here
    }
    if (status === 6) return 'approved'; // Ready for signing
    if (status === 7) return 'signing';
    if (status >= 8) return 'drafting';
  
    return 'editing';
  }

  getStateText():{item: string, allowed: boolean} {
    const state = this.getComputedApprovalState();

    switch (state) {
      case 'editing':
        return {
          item: 'Binder is in Editing Mode',
          allowed: true,
        };
      case 'pending': 
        return {
          item: 'Binder is pending approval',
          allowed: false,
        };
      case 'rejected':
        return {
          item: 'Binder has been rejected',
          allowed: false,
        };
      case 'approved':
        return {
          item: 'Binder has been approved',
          allowed: false,
        };
      case 'signing':
        return {
          item: 'Binder is being signed',
          allowed: false,
        };
      case 'drafting':
        return {
          item: 'Binder has been sent to drafting',
          allowed: false,
        };
      case 'cancelled':
        return {
          item: 'Binder has been cancelled',
          allowed: false,
        };
      default:
        return {
          item: 'Binder is in Editing Mode',
          allowed: true,
        };
    }
  }

  getBannerState() {
    const state = this.getComputedApprovalState();
    const userHasApprovalAccess = this.authService.checkForPerm('approvalResponses', 'edit', this.currentBinderService.currentBinder().SelectedFID);

    switch (state) {
      case 'editing':
        return { show: false };
      case 'pending': {
        const actions: string[] = ['Cancel Request'];
        let message = 'This binder has been submitted for approval and pending a response.';
        if (userHasApprovalAccess) {
          actions.push('Respond');
          message = 'This binder has been submitted for approval. Please respond to the request.';
        }
        return {
          show: true,
          text: message,
          class: 'pending',
          severity: 'info',
          actions
        };
      }
      case 'rejected':
        return {
          show: true,
          text: 'This binder has been rejected.',
          class: 'rejected',
          severity: 'error',
          actions: ['Make Changes'] as string[]
        };
      case 'approved':
        return {
          show: true,
          text: 'This binder has been approved.',
          class: 'approved',
          severity: 'success',
          actions: ['Request Signatures', 'Cancel Request'] as string[]
        };
      case 'signing':
        return {
          show: true,
          text: 'This binder is being signed.',
          class: 'signing',
          severity: 'warning',
          actions: ['Cancel Signing'] as string[]
        };
      case 'drafting':
        return {
          show: true,
          text: 'This binder has been sent to drafting.',
          class: 'drafting',
          severity: 'success',
          actions: ['Cancel Drafting'] as string[]
        };
      default:
        return {
          show: false,
          text: 'Approval state unknown.',
          class: 'default',
          severity: 'info',
          actions: [] as string[]
        };
    }
  }

  get approvable() {
    return this.authService.checkForPerm('approvalResponses', 'edit', this.currentBinderService.currentBinder().SelectedFID);
  }

  requestApproval(binderId:number,users:any[],clientLastName:string): Observable<any>  {

    const userData = users.map(user => ({DisplayName:user.DisplayName,User:user.User,UID:user.UID}));
    const requestBody = {
      BinderID: binderId,
      users:userData,
      clientName:clientLastName
    }
    return this.http.post<any>(`${this.baseUrl}/request`, requestBody);
  }

  cancelApproval(approalId:number,binderId:number): Observable<any>  {
    return this.http.post<any>(`${this.baseUrl}/cancel/${approalId}`,{binderId:binderId});
  }

  respondToApproval(response:response): Observable<any>  {
    return this.http.post<any>(`${this.baseUrl}/respond`, response);
  }

  setRequest(request: ApprovalRequest, ProjectStatus:number) {
    this.currentRequest.set(request);
    if(ProjectStatus === 7) {
      this.approvalState.set('signing');
    }
    else if (ProjectStatus > 7) {
      this.approvalState.set('drafting');
    }
    else if(request.cancelled) {
      this.approvalState.set('cancelled');
      this.currentRequest.set(null);
    }
    else {
      if (request.approvalResponses && request.approvalResponses.length > 0) {
        const latestResponse = request.approvalResponses.reduce((latest, current) => 
          new Date(latest.responseDate) > new Date(current.responseDate) ? latest : current
        );
        if (latestResponse.approved) {
          this.approvalState.set('approved');
        } else {
          this.approvalState.set('rejected');
        }
      } else {
        this.approvalState.set('pending');
      }
    }

  }

  cancelRequest() {
    this.currentRequest.set(null);
    this.approvalState.set('cancelled');
  }
}

